import {
  CalendarDays,
  Construction,
  Landmark,
  Layers,
  Map as MapIcon,
  Maximize2,
  TrainFront,
  type LucideIcon,
} from 'lucide-react';
import { lazy, Suspense, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { MapLayerId } from '../../../shared/domain/index.ts';
import { useTheme } from '../../lib/theme.ts';
import { MAP_LAYERS } from '../map/layers.ts';

const MapView = lazy(() => import('../map/MapView.tsx').then((m) => ({ default: m.MapView })));

/** Calques proposés sur l'accueil. Les signalements (non ouverts, ADR-0010) et les commerces (denses) restent sur /carte. */
const PREVIEW: readonly { id: MapLayerId; icon: LucideIcon }[] = [
  { id: 'transport', icon: TrainFront },
  { id: 'roadworks', icon: Construction },
  { id: 'events', icon: CalendarDays },
  { id: 'services', icon: Landmark },
];

type Filter = 'all' | MapLayerId;

/**
 * Aperçu de la carte : filtre à choix unique (« Tous » ou une catégorie), comme sur le mockup.
 * MapLibre (~250 Ko) est chargé à part : l'accueil s'affiche sans l'attendre.
 */
export function CityMapPanel() {
  const theme = useTheme();
  const [filter, setFilter] = useState<Filter>('all');
  const enabled = useMemo<ReadonlySet<MapLayerId>>(
    () => new Set(filter === 'all' ? PREVIEW.map((p) => p.id) : [filter]),
    [filter],
  );
  const labelOf = (id: MapLayerId) => MAP_LAYERS.find((l) => l.id === id)?.label ?? id;
  const colorOf = (id: MapLayerId) => MAP_LAYERS.find((l) => l.id === id)?.color;

  return (
    <section className="card map-panel" aria-labelledby="map-panel-title">
      <div className="card__header">
        <h2 className="card__title" id="map-panel-title">
          <MapIcon size={22} aria-hidden="true" />
          Carte d’Ermont
        </h2>
        <div className="card__actions">
          <Link to="/carte" className="button button--outline button--sm">
            <Maximize2 size={16} aria-hidden="true" />
            <span className="button__label-long">Voir en plein écran</span>
            <span className="button__label-short">Plein écran</span>
          </Link>
        </div>
      </div>
      <Suspense fallback={<div className="map-frame map-frame--preview" aria-busy="true" />}>
        <MapView enabled={enabled} variant="preview" dark={theme === 'dark'} />
      </Suspense>
      <div className="chips" role="group" aria-label="Filtrer la carte">
        <button type="button" className="chip" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
          <Layers size={16} aria-hidden="true" />
          Tous
        </button>
        {PREVIEW.map(({ id, icon: Icon }) => (
          <button key={id} type="button" className="chip" aria-pressed={filter === id} onClick={() => setFilter(id)}>
            <Icon size={16} aria-hidden="true" style={{ color: filter === id ? undefined : colorOf(id) }} />
            {labelOf(id)}
          </button>
        ))}
      </div>
    </section>
  );
}
